import type {
  BriefViewRequest,
  ImportRowParams,
  KnowledgeSearchParams,
  OrganisationListParams,
  SubjectListParams,
} from './client';

/**
 * Query keys. Every key carries the org id: switching orgs (or, in dev,
 * switching users) must never reuse another context's cache.
 */
export const qk = {
  /** Reports (reports design §2). One prefix, so a write moves the list and the page. */
  reports: (orgId: string) => ['reports', orgId, 'list'] as const,
  report: (orgId: string, reportId: string) => ['reports', orgId, 'one', reportId] as const,
  reportsPrefix: (orgId: string) => ['reports', orgId] as const,
  /** Events (event graph design §3a). One prefix, so a write moves the list and the page. */
  events: (orgId: string, params: object) => ['events', orgId, 'list', params] as const,
  event: (orgId: string, entityId: string) => ['events', orgId, 'one', entityId] as const,
  eventsPrefix: (orgId: string) => ['events', orgId] as const,
  eventAttendee: (orgId: string, entityId: string, nodeId: string) =>
    ['events', orgId, 'one', entityId, 'attendee', nodeId] as const,
  eventAttendees: (orgId: string, entityId: string, params: object) =>
    ['events', orgId, 'one', entityId, 'attendees', params] as const,
  me: () => ['me'] as const,
  /**
   * Home's short recent list — one page of the org's people, no paging.
   *
   * The parameters are the key rather than a search string, because there are
   * five ways to ask now and only one of them is the text. Two reads that asked
   * for the same thing share a key however their controls were arrived at, which
   * is why `peopleParams` drops anything at its default before it gets here.
   */
  subjects: (orgId: string, params: SubjectListParams) =>
    ['subjects', orgId, 'page', params] as const,
  /**
   * The People screen's paged read. A separate leaf from Home's, because an
   * infinite query's cache entry is a list of pages and a plain one's is a
   * single answer — the same key for both would hand one the other's shape.
   * Still under the same prefix, so one invalidation moves both.
   */
  people: (orgId: string, params: SubjectListParams) =>
    ['subjects', orgId, 'paged', params] as const,
  /** Every read of the org's people, however asked — an invalidation prefix. */
  subjectsPrefix: (orgId: string) => ['subjects', orgId] as const,
  record: (orgId: string, subjectId: string) => ['record', orgId, subjectId] as const,
  /**
   * Every record read in the org — an invalidation prefix. Written for the
   * likely-merge answer, which has no subject of its own to refresh and can move
   * any of them: the fold rewrites one node's connections onto another, and a
   * record open on either side is describing the world before that.
   */
  recordPrefix: (orgId: string) => ['record', orgId] as const,
  /**
   * The view belongs in the key: the stable brief and the live draft are two
   * different dossiers for the same subject (§3e), and caching them as one
   * would show the reader whichever they asked for last.
   */
  brief: (orgId: string, subjectId: string, view: BriefViewRequest) =>
    ['brief', orgId, subjectId, view] as const,
  /** Both views of one subject's brief — an invalidation prefix. */
  briefPrefix: (orgId: string, subjectId: string) => ['brief', orgId, subjectId] as const,
  subjectData: (orgId: string, subjectId: string) => ['subject-data', orgId, subjectId] as const,
  /** Every subject-data read in the org — an invalidation prefix. */
  subjectDataPrefix: (orgId: string) => ['subject-data', orgId] as const,
  network: (orgId: string, subjectId: string) => ['network', orgId, subjectId] as const,
  /** Every network read in the org — an invalidation prefix. */
  networkPrefix: (orgId: string) => ['network', orgId] as const,
  runHistory: (orgId: string, subjectId: string) => ['run-history', orgId, subjectId] as const,
  /** Under run history's key, so whatever refreshes the history refreshes this. */
  runFindings: (orgId: string, subjectId: string) =>
    ['run-history', orgId, subjectId, 'findings'] as const,
  run: (orgId: string, runId: string) => ['run', orgId, runId] as const,
  decision: (orgId: string, decisionId: string) => ['decision', orgId, decisionId] as const,
  /** Every decision read in the org — an invalidation prefix. */
  decisionPrefix: (orgId: string) => ['decision', orgId] as const,
  decisionLog: (orgId: string, subjectId: string) => ['decision-log', orgId, subjectId] as const,
  /**
   * Every record's History in the org — an invalidation prefix, and a different
   * family from `decisionPrefix`: one decision read is `['decision', org, id]`
   * and a log is `['decision-log', org, id]`, so neither prefix reaches the
   * other. Written for the by-hand join, which files its rows under whichever of
   * two records survived — not necessarily the one whose page was open.
   */
  decisionLogPrefix: (orgId: string) => ['decision-log', orgId] as const,
  needsAction: (orgId: string) => ['needs-action', orgId] as const,
  factClaims: (orgId: string, lineageId: string) => ['fact-claims', orgId, lineageId] as const,
  orgSettings: (orgId: string) => ['org-settings', orgId] as const,
  credits: (orgId: string) => ['credits', orgId] as const,
  connector: (orgId: string) => ['connector', orgId] as const,

  /**
   * **The knowledge base's search** — the parameters are the key, as they are on
   * the People screen, so the page as it opens (no `q`, no chips) and a search
   * are two entries and neither can be shown in place of the other.
   */
  knowledge: (orgId: string, params: KnowledgeSearchParams) =>
    ['knowledge', orgId, 'search', params] as const,
  /** One thing's panel, keyed on the entity the whole page is keyed on. */
  knowledgePanel: (orgId: string, entityId: string) =>
    ['knowledge', orgId, 'panel', entityId] as const,
  /** The footer's whole graph, per account in a security org. */
  knowledgeGraph: (orgId: string, accountId: string | null) =>
    ['knowledge', orgId, 'graph', accountId] as const,
  /**
   * **What a join by hand would do** (fold-tracked-people note §7), keyed on the
   * pair **and the direction**: the sentence is what happens to *these two in
   * this order*, so the two directions are two entries and neither can be shown
   * in place of the other. Flipping the radio changes the key, which is the
   * whole of how the modal re-asks.
   */
  knowledgeJoinPreview: (orgId: string, entityId: string, otherEntityId: string, keep: string) =>
    ['knowledge', orgId, 'join-preview', entityId, otherEntityId, keep] as const,
  /**
   * Every read of the knowledge base — an invalidation prefix. Written for the
   * likely-merge answer, which folds one node into another and so changes both
   * the search and any panel open on either side of it.
   */
  knowledgePrefix: (orgId: string) => ['knowledge', orgId] as const,

  /**
   * **The organisations roster** (organisations-as-subjects note §1 R8). The
   * parameters are the key, as they are on the People screen, so a search is a
   * different read that begins at its own first page.
   *
   * A leaf of its own rather than a branch of `subjects`: an organisation record
   * is not a `SubjectRecord`, so nothing here can be shown in place of anything
   * there, and one invalidation must never move both.
   */
  organisations: (orgId: string, params: OrganisationListParams) =>
    ['organisations', orgId, 'paged', params] as const,
  /** Every read of the org's organisations, however asked — an invalidation prefix. */
  organisationsPrefix: (orgId: string) => ['organisations', orgId] as const,
  /**
   * One organisation's record, keyed on its entity id — which is also its record
   * row's primary key, so there is only ever one id in play.
   */
  organisation: (orgId: string, entityId: string) => ['organisation', orgId, entityId] as const,

  /** The Imports ledger — one read, no paging (import note §2.4). */
  imports: (orgId: string) => ['imports', orgId] as const,
  /** One session, polled while it is in progress. */
  importSession: (orgId: string, importId: string) => ['import', orgId, importId] as const,
  /**
   * The People tab's rows. The filter is the key, so a chip is a different query
   * that begins at page one — the same property the People screen's key has.
   */
  importRows: (orgId: string, importId: string, params: ImportRowParams) =>
    ['import-rows', orgId, importId, params] as const,
  /** Every filtered read of one session's rows — an invalidation prefix. */
  importRowsPrefix: (orgId: string, importId: string) => ['import-rows', orgId, importId] as const,
};

/** ui-design §4.3: 3s while a run is working or resuming. */
export const RUN_POLL_MS = 3_000;

/** ui-design §4.1 / §4.3: everything else is 60s plus window focus. */
export const PAGE_POLL_MS = 60_000;

/**
 * How long the People screen's paged read counts as fresh — the one read in the
 * app with a `staleTime` of its own, because it is the one read whose refetch is
 * every page the reader has loaded rather than one request.
 *
 * The same minute the rest of the app refreshes on, spent the other way round:
 * where `PAGE_POLL_MS` says "ask again after a minute", this says "do not ask
 * again within a minute". A write still moves the list at once — an invalidation
 * refetches an active query however fresh it is (see {@link usePeople}).
 */
export const PEOPLE_STALE_MS = 60_000;
